export const anatomyModule = {
  id: "anatomy",
  title: "Adrenal Gland Anatomy: Part 1",
  sections: [
    {
      type: "text",
      content: `
        <h3>Overview</h3>
        <p>
       The adrenal glands (sometimes called suprarenal glands) are small <b>yellowish-tan</b>, triangular-shaped glands located in the 
       <b>retroperitoneal cavity</b> on top of each kidney. 
        They weight about <b>4 grams</b> each in adults, and measure approximately <b>5 x 2 x 1 cm</b>.</p>
        <p>They are often covered by a thin layer of connective tissue called the <b>renal fascia</b> (also called Gerota's fascia),
         which helps anchor them to the kidneys.
        Ontop of that, they are surrounded by a layer of fat that provides cushioning and protection.</p>
        <div class="highlight-box">
          <strong>Quick Facts:</strong>
          <ul>
            <li>📏 Size: About 5 cm long and 2-3 cm wide</li>
            <li>⚖️ Weight: 4 grams each</li>
            <li>📍 Location: Superior pole of each kidney</li>
            <li>🎨 Color: Yellowish-tan in appearance</li>
          </ul>
        </div>
        <p>
        They are part of the <b>endocrine system</b> and play a crucial role in producing hormones that regulate various bodily functions,
         including metabolism, immune response, blood pressure, and stress response.</p>
        <img src="/images/adrenalglands/adrenalglandoverviewanatomy.png" alt="Detail" style="width: 100%; border-radius: 8px; margin: 16px 0;" />
    
        <h3>Structure</h3>
        <p>Each adrenal gland consists of three distinct parts, that are easily verified macroscopically (looking at the organ with bare 👁️👁️):</p>
        <ul>
          <li><b>Adrenal Capsule:</b> A thin outer layer of connective tissue that surrounds and protects the gland. About 0.5 mm thick.</li>
          <li><b>Adrenal Cortex:</b> The outer layer, producing <b>steroid hormones</b> 
          (hormones such as cortisol, aldosterone, and androgens). 
          This layer gives the gland its yellowish-tan color due to the presence of lipids, as the cells store cholesterol to build these hormones.
          <ol>Derived from the <b>mesoderm</b> during embryonic development.</ol>
          </li>
          <li><b>Adrenal Medulla:</b> The inner core, producing catecholamines (e.g., adrenaline and noradrenaline). 
          This layer gives the gland its reddish-brown color due to the presence of chromaffin cells, 
          that are cells that contain <b>catecholamines (e.g., adrenaline and noradrenaline)</b>. 
          Here you will also find blood vessels and nerve fibers.
          <ol>Derived from the <b>neural crest</b> during embryonic development.</ol>
          </li>
        </ul>      
      `,
      image: "/images/adrenalglands/layers.png",
    },
    {
      type: "question",
      text: "Are the adrenal glands located retroperitoneally?",
      options: [
        "Yes, they are located retroperitoneally",
        "No, they are located intraperitoneally",
      ],
      correct: 0,
      explanation:
        "The adrenal glands are located in the retroperitoneal cavity, on top of each kidney.",
    },
    {
      type: "question",
      text: "What are the three parts of the adrenal gland?",
      options: [
        "Fibrous shell, cortex & medulla",
        "Capsule, cortex & medulla",
        "Capsule, cortex & mizdulla",
        "Mizdull, Medulla & outer core",
      ],
      correct: 1,
      explanation:
        "The adrenal gland consists of the adrenal capsule, adrenal cortex, and the adrenal medulla.",
      explanationImage: "/images/adrenalglands/layers.png",
    },
    {
      type: "question",
      text: "What gives the adrenal medulla its reddish-brown color?",
      options: [
        "Blood vessels",
        "Iron deposits",
        "Chromaffin cells",
        "Muscle tissue",
      ],
      correct: 2,
      explanation:
        "The adrenal medulla's reddish-brown color is due to the presence of chromaffin cells, which contain catecholamines.",
    },
    {
      type: "question",
      text: "How much does an adult adrenal gland weigh?",
      options: ["2 grams", "4 grams", "6 grams", "8 grams"],
      correct: 1,
      explanation: "An adult adrenal gland typically weighs about 4 grams.",
    },
    {
      type: "question",
      text: "What is the name of the connective tissue layer that covers the adrenal glands?",
      options: [
        "Renal fascia",
        "Gland fascia",
        "Perirenal fat",
        "Adrenal capsule",
      ],
      correct: 0,
      explanation:
        "The adrenal glands are covered by a thin layer of connective tissue called the renal fascia, also known as Gerota's fascia.",
    },
    {
      type: "question",
      text: "Why are the cells of the cortex filled with lipids?",
      options: [
        "To store energy",
        "To protect against stress",
        "To aid in blood flow",
        "To build steroid hormones",
      ],
      correct: 3,
      explanation:
        "The cells of tellellehe adrenal cortex are filled with lipids because they use cholesterol to build steroid hormones such as cortisol, aldosterone, and androgens.",
    },
    {
      type: "question",
      text: "What part of the adrenal gland is derived from the neural crest during embryonic development?",
      options: [
        "Adrenal capsule",
        "Adrenal cortex",
        "Adrenal medulla",
        "Renal fascia",
      ],
      correct: 2,
      explanation:
        "The cells of the adrenal medulla are derived from the neural crest during embryonic development, and the cortex is derived from the mesoderm.",
    },
    {
      type: "question",
      text: "What is the adrenal cortex derived from during embryonic development?",
      options: ["Mesoderm", "Ectoderm", "Endoderm", "Neural crest"],
      correct: 0,
      explanation:
        "The cells of the adrenal medulla are derived from the neural crest during embryonic development, and the cortex is derived from the mesoderm.",
    },
    {
      type: "text",
      content: `
        <h3>Blood supply: Arteries</h3>
        <p>Blood supply to the adrenal glands is rich and comes from three main arteries:</p>
        <ul>
          <li><b>Superior suprarenal arteries:</b> Branches of the inferior phrenic arteries. The inferior phrenic artery branches off the abdominal aorta just above the celiac trunk
          .</li>
          <li><b>Middle suprarenal arteries:</b> Direct branches from the abdominal aorta, branches approximately at the level of 
          the superior mesenteric artery which is below the celiac trunk and around the level of L1 vertebra
          .</li>
          <li><b>Inferior suprarenal arteries:</b> Branches from the renal arteries.</li>
        </ul> 
        <p>These arteries form a dense network of capillaries within the adrenal glands, ensuring an 
        adequate supply of oxygen and nutrients to support their hormone-producing functions.</p>
      `,
      image: "/images/adrenalglands/adrenalglandarteries.png",
    },
    {
      type: "question",
      text: "what arteries supply the adrenal glands?",
      options: [
        "Renal arteries, Gonadal arteries, Inferior phrenic arteries",
        "Renal arteries, Middle suprarenal arteries, Superior mesenteric arteries",
        "Inferior phrenic arteries, Abdominal aorta, Renal arteries",
        "Superior suprarenal arteries, Middle suprarenal arteries, Inferior suprarenal arteries",
      ],
      correct: 3,
      explanation:
        "The adrenal glands receive blood supply from the superior suprarenal arteries (from the inferior phrenic arteries), middle suprarenal arteries (from the abdominal aorta), and inferior suprarenal arteries (from the renal arteries).",
    },
    {
      type: "question",
      text: "What artery that supplies the adrenal gland is derived from the inferior phrenic artery?",
      options: [
        "Superior suprarenal artery",
        "Middle suprarenal artery",
        "Inferior suprarenal artery",
        "Renal artery",
      ],
      correct: 0,
      explanation:
        "The superior suprarenal artery is derived from the inferior phrenic artery.",
      image: "/images/adrenalglands/adrenalglandarteries1.png",
    },
    {
      type: "question",
      text: "What artery that supplies the adrenal gland is derived from the renal artery?",
      options: [
        "Superior suprarenal artery",
        "Middle suprarenal artery",
        "Inferior suprarenal artery",
        "Renal artery",
      ],
      correct: 2,
      explanation:
        "The inferior suprarenal artery is derived from the renal artery.",
      image: "/images/adrenalglands/adrenalglandarteries1.png",
    },
    {
      type: "question",
      text: "What artery that supplies the adrenal gland is derived from the abdominal aorta?",
      options: [
        "Superior suprarenal artery",
        "Middle suprarenal artery",
        "Inferior suprarenal artery",
        "Renal artery",
      ],
      correct: 1,
      explanation:
        "The middle suprarenal artery is derived from the abdominal aorta.",
      image: "/images/adrenalglands/adrenalglandarteries1.png",
    },
    {
      type: "text",
      content: `
        <h3>Blood supply: Veins</h3>  
        <p>Venous drainage from the adrenal glands is primarily through the <b>suprarenal veins</b>. 
        <ul>
          <li><b>Right suprarenal vein:</b> Drains directly into the inferior vena cava.</li>
          <li><b>Left suprarenal vein:</b> Drains into the left renal vein.</li>
        </ul>
      `,
      image: "/images/adrenalglands/adrenalglandveins.png",
    },
    {
      type: "question",
      text: "What veins drain the adrenal glands?",
      options: [
        "Inferior vena cava, right renal vein, left suprarenal vein",
        "Inferior vena cava, right suprarenal vein",
        "Renal veins",
        "Right suprarenal vein, Left suprarenal vein",
      ],
      correct: 3,
      explanation:
        "The adrenal glands are drained by the right suprarenal vein (which drains into the inferior vena cava) and the left suprarenal vein (which drains into the left renal vein).",
      image: "/images/adrenalglands/adrenalglandveins1.png",
    },
    {
      type: "text",
      content: `
        <h3>Nerve supply</h3>
        <p>The cortical part of the adrenal gland is primarily regulated by <b>hormonal signals</b>, particularly adrenocorticotropic hormone (ACTH) from the anterior pituitary gland.
        <p>However, the adrenal medulla receives direct <b>sympathetic innervation</b> from the autonomic nervous system, as it is involved in the body's "fight or flight" response, and
        therefore needs to respond quickly to stress signals and secrete catecholamines (adrenaline and noradrenaline) into the bloodstream. 
        </p>  
        <p>The sympathetic supply comes from <span class="glossary-term">preganglionic fibers (type B)<span class="tooltip">the type that release acetylcholine, moderately myelinated, slower signaling than type A (fast, heavily myelinated, motor neurons)</span></span>, 
         that originate in the <strong>thoracic spinal cord (T5-T11)</strong> and some synapse directly onto the 
        chromaffin cells of the adrenal medulla, meaning that these cells act like postganglionic neurons (neurons that release norepinephrine or epinephrine as neurotransmitters at the target organ) 
        ), releasing catecholamines directly into the bloodstream as hormones.   
       These preganglionic fibers travel through the sympathetic chain without synapsing and reach the adrenal medulla via the <b>greater splanchnic nerve</b> 
       or in lesser extent via the <b>lesser splanchnic nerve</b> and <b>least splanchnic nerve</b>.
        </p>
         <ul>
          <li>Thoracic spinal cord (T5-T11) => Greater splanchnic nerve (from the sympathetic trunk) => Celiac plexus => Synapse directly on chromaffin cells in adrenal medulla => Release of catecholamines (adrenaline and noradrenaline) into bloodstream as hormones.
            </li>
        </ul>
      `,
      image: "/images/adrenalglands/adrenalglandinnervation.png",
    },
    {
      type: "question",
      text: "Where does the neural supply for the adrenal glands come from?",
      options: [
        "Celiac plexus and renal plexus",
        "Inferior mesenteric plexus",
        "Thoracic spinal cord (T5-T11)",
        "Lumbar spinal cord (L1-L3)",
      ],
      correct: 2,
      explanation:
        "The adrenal medulla receives sympathetic innervation from preganglionic fibers originating in the thoracic spinal cord (T5-T11) via the greater splanchnic nerve.",
    },
    {
      type: "question",
      text: "Which part of the adrenal gland is primarily regulated by hormonal signals rather than direct neural innervation?",
      options: [
        "Adrenal medulla",
        "Adrenal cortex",
        "Adrenal capsule",
        "Renal fascia",
      ],
      correct: 1,
      explanation:
        "The adrenal cortex is primarily regulated by hormonal signals, particularly adrenocorticotropic hormone (ACTH) from the anterior pituitary gland.",
    },
    {
      type: "question",
      text: "Through which nerve do the preganglionic fibers primarily reach the adrenal medulla?",
      options: [
        "Lesser splanchnic nerve",
        "Greater splanchnic nerve",
        "Least splanchnic nerve",
        "Vagus nerve",
      ],
      correct: 1,
      explanation:
        "The preganglionic fibers reach the adrenal medulla primarily via the greater splanchnic nerve.",
    },
    {
      type: "question",
      text: "What neurotransmitter is released by the preganglionic fibers that innervate the adrenal medulla?",
      options: ["Norepinephrine", "Acetylcholine", "Dopamine", "Epinephrine"],
      correct: 1,
      explanation:
        "The preganglionic fibers that innervate the adrenal medulla release acetylcholine, which stimulates the chromaffin cells to secrete catecholamines.",
    },
    {
      type: "text",
      content: `
        <p>Adrenal gland: Function within the layers and zones.</p>
        <p><h3>The adrenal capsule</h3> is a thin layer of connective tissue that surrounds and protects the adrenal gland.</p>
        <p><h3>The adrenal cortex</h3> produces three main types of hormones:</p>
        <ol>
         <li><b>Zona glomerulosa:</b> The outermost layer, responsible for producing <b>mineralocorticoids</b>, such as <b class="purple-term">aldosterone</b>. 
         Aldosterone acts on the kidneys, more specifically on the distal tubules and collecting ducts, where it promotes sodium retention
          (and with that water reabsorption) and potassium excretion. This leads to an increase in blood volume and thus a higher blood pressure.</li>
        <li><b>Zona fasciculata:</b> The middle layer, produces <b>glucocorticoids</b>, such as <b class="purple-term">cortisol</b>. 
        Cortisol is secreted in response to stress and low blood-glucose levels, and it has several important functions, including increasing 
        blood sugar through gluconeogenesis (in the liver), suppressing the immune system (anti-inflammatory effects), and aids in fat, protein, 
        and carbohydrate metabolism. Too much cortisol can lead to Cushing's syndrome, while too little can cause Addison's disease, but more on that in later modules.</li>
        <li><b>Zona reticularis:</b> The innermost layer, responsible for producing <b>androgens</b>, mainly <b class="purple-term">dehydroepiandrosterone (DHEA)</b> and <b class="purple-term">androstenedione</b>. 
        These hormones are then converted into more potent androgens, such as testosterone, and estrogens in the gonads and other tissues. 
        Androgens play a role in the development of secondary sexual characteristics and influence libido.</li> 
        </ol>
        <p><h3>The adrenal medulla</h3> is responsible for producing catecholamines, including:</p>
        <ul>
          <li><b class="purple-term">Adrenaline (epinephrine):</b> This hormone is released in response to stress and helps prepare the body for a "fight or flight" 
          response by increasing heart rate, blood pressure, and blood flow to muscles. It also helps increase blood sugar levels by promoting glycogenolysis 
          in the liver.</li>
          <li><b class="purple-term">Noradrenaline (norepinephrine):</b> This hormone works alongside adrenaline to increase heart rate and blood pressure, and it 
          also helps regulate blood vessel constriction.</li>
        </ul>
      `,
      image: "/images/adrenalglands/adrenalglandlayers.png",
    },
    {
      type: "text",
      content: `
        <h3>To recap...</h3>
       
 <div class="mnemonic-card">
      <div class="mnemonic-title">🎯 Memory Trick</div>
      <div class="mnemonic-content">
        <strong>"GFR + M"</strong>
        <ul>
          <li><b><strong>G</strong>lomerulosa</b> => <strong>Salt</strong> (<b>Aldosterone</b>)</li> 
          <li><b><strong>F</strong>asciculata</b> => <strong>Sugar</strong> (<b>Cortisol</b>)</li>
          <li><b><strong>R</strong>eticularis</b> => <strong>Sex</strong> (<b>Androgens</b>)</li>
          <li><b><strong>M</strong>edulla</b> => <strong>Stimulation</strong> (<b>Epinephrine</b> and <b>Norepinephrine</b>)</li>
        </ul>
      </div>
    </div> 
      `,
      image: "/images/adrenalglands/adrenalglandmemo.png",
    },
    {
      type: "question",
      text: "Which hormone is produced by the adrenal medulla?",
      options: ["Cortisol", "Aldosterone", "Adrenaline", "Testosterone"],
      correct: 2,
      explanation:
        'Adrenaline (epinephrine) is produced by the adrenal medulla and is responsible for the "fight or flight" response.',
    },
    {
      type: "question",
      text: "What is the primary function of aldosterone produced by the zona glomerulosa?",
      options: [
        "Regulate blood sugar levels",
        "Promote sodium retention and potassium excretion",
        "Stimulate immune response",
        "Increase heart rate",
      ],
      correct: 1,
      explanation:
        "Aldosterone promotes sodium retention and potassium excretion in the kidneys, leading to increased blood volume and blood pressure.",
    },
    {
      type: "question",
      text: "Which layer of the adrenal cortex produces glucocorticoids like cortisol?",
      options: [
        "Zona glomerulosa",
        "Zona fasciculata",
        "Zona reticularis",
        "Adrenal medulla",
      ],
      correct: 1,
      explanation:
        "The zona fasciculata is the middle layer of the adrenal cortex that produces glucocorticoids such as cortisol.",
    },
    {
      type: "question",
      text: "What hormones are primarily produced by the zona reticularis?",
      options: [
        "Mineralocorticoids",
        "Glucocorticoids",
        "Androgens",
        "Catecholamines",
      ],
      correct: 2,
      explanation:
        "The zona reticularis produces androgens, such as dehydroepiandrosterone (DHEA) and androstenedione.",
    },
  ],
};
export default anatomyModule;
